import {
  safeGetLocalStorage,
  safeRemoveLocalStorage,
  safeSetLocalStorage,
} from '@/lib/utils/storageUtils';
import {
  cloneJournal,
  createEmptyJournal,
  decodeQuizRunJournal,
  encodeQuizRunJournal,
  type QuizRunJournal,
} from './codec';
import {
  QuizRunPersistenceCancelledError,
  QuizRunPersistenceError,
  RETRY_DELAYS_MS,
  type CanonicalItem,
  type CompletionItem,
  type PersistedItem,
  type PersistedPollItem,
  type PersistedTriviaItem,
  type PollSnapshot,
  type QuizCompletion,
  type QuizItemProgressSnapshot,
  type QuizRunPersistenceIdentity,
  type QuizRunPersistenceCancellationReason,
  type QuizRunPersistence,
  type QuizRunPersistenceLifecycle,
  type QuizRunPersistenceOptions,
  type QuizRunPersistenceScheduler,
  type QuizRunPersistenceSnapshot,
  type QuizRunPersistenceStorage,
  type QuizRunPersistenceTransport,
  type TriviaSnapshot,
} from './types';

import {
  isSequencePrefix,
  isValidQuizItemProgressSequence,
  reconcileQuizItemProgress,
} from './progress';

export * from './types';
export * from './codec';
export { isValidQuizItemProgressSequence, reconcileQuizItemProgress } from './progress';

const DEFAULT_SCHEDULER: QuizRunPersistenceScheduler = {
  setTimeout: (callback, delayMs) => globalThis.setTimeout(callback, delayMs),
  clearTimeout: (handle) => globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>),
};

const DEFAULT_LIFECYCLE: QuizRunPersistenceLifecycle = {
  addEventListener: (event, listener) => {
    if (typeof globalThis.addEventListener === 'function') {
      globalThis.addEventListener(event, listener);
    }
  },
  removeEventListener: (event, listener) => {
    if (typeof globalThis.removeEventListener === 'function') {
      globalThis.removeEventListener(event, listener);
    }
  },
};

// Browser storage can be denied in embeds or become unavailable during a run.
// Keep server persistence working even when the local recovery journal cannot be saved.
const BROWSER_STORAGE: QuizRunPersistenceStorage = {
  getItem: safeGetLocalStorage,
  setItem: safeSetLocalStorage,
  removeItem: safeRemoveLocalStorage,
};

type PendingOperation = {
  itemId: string;
  revision: number;
  promise: Promise<unknown>;
  transportDone: Promise<void>;
  run: () => Promise<unknown>;
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
  resolveTransport: () => void;
  rejectTransport: (reason: unknown) => void;
  attempts: number;
  timer: unknown | null;
  parked: boolean;
  settled: boolean;
  transportFinished: boolean;
  inFlight: boolean;
  superseded: boolean;
};

type CompletionWaiter = {
  resolve: (completion: QuizCompletion | null) => void;
  reject: (reason: unknown) => void;
};

/**
 * Versioned storage keys are intentionally scoped to the canonical identity.
 * The generation is not durable: it is only an in-memory stale-work fence.
 */
export function buildQuizRunPersistenceStorageKey(input: {
  quizId: string;
  editionId: string;
  userId: string;
  isAnonymous: boolean;
}): string {
  const part = (value: string): string => encodeURIComponent(value);
  return [
    'atrivial',
    'quiz-run-persistence',
    'v1',
    part(input.quizId),
    part(input.editionId),
    input.isAnonymous ? 'anonymous' : 'authenticated',
    part(input.userId),
  ].join(':');
}

function ensureNonEmpty(value: unknown, field: string): asserts value is string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new QuizRunPersistenceError(`${field} is required`, {
      code: 'quiz_run_snapshot_invalid',
    });
  }
}

function ensureNonNegativeInteger(value: unknown, field: string): asserts value is number {
  if (!Number.isInteger(value) || (value as number) < 0) {
    throw new QuizRunPersistenceError(`${field} must be a non-negative integer`, {
      code: 'quiz_run_snapshot_invalid',
    });
  }
}

function validateTriviaSnapshot(snapshot: TriviaSnapshot): void {
  ensureNonEmpty(snapshot.editionItemId, 'editionItemId');
  if (!Array.isArray(snapshot.guessSequence) || snapshot.guessSequence.length === 0) {
    throw new QuizRunPersistenceError('guessSequence must not be empty', {
      code: 'quiz_run_snapshot_invalid',
    });
  }
  const guesses = [...snapshot.guessSequence];
  if (
    guesses.some((guess) => !Number.isInteger(guess) || guess < 0 || guess > 3) ||
    new Set(guesses).size !== guesses.length
  ) {
    throw new QuizRunPersistenceError('guessSequence contains an invalid or duplicate guess', {
      code: 'quiz_run_snapshot_invalid',
    });
  }
  ensureNonNegativeInteger(snapshot.xpEarned, 'xpEarned');
  validateFeedback(snapshot.rating, 'rating');
  validateFeedback(snapshot.feedback, 'feedback');
  if (
    snapshot.feedbackText !== undefined &&
    snapshot.feedbackText !== null &&
    typeof snapshot.feedbackText !== 'string'
  ) {
    throw new QuizRunPersistenceError('feedbackText must be a string or null', {
      code: 'quiz_run_snapshot_invalid',
    });
  }
}

function validateFeedback(value: number | null | undefined, field: string): void {
  if (
    value !== undefined &&
    value !== null &&
    (!Number.isInteger(value) || value < -32768 || value > 32767)
  ) {
    throw new QuizRunPersistenceError(`${field} must be a nullable smallint`, {
      code: 'quiz_run_snapshot_invalid',
    });
  }
}

function validatePollSnapshot(snapshot: PollSnapshot): void {
  ensureNonEmpty(snapshot.editionItemId, 'editionItemId');
  ensureNonEmpty(snapshot.pollDefinitionId, 'pollDefinitionId');
  ensureNonEmpty(snapshot.optionId, 'optionId');
  if (snapshot.sessionId !== undefined && snapshot.sessionId !== null) {
    ensureNonEmpty(snapshot.sessionId, 'sessionId');
  }
  if (snapshot.xpEarned !== undefined) ensureNonNegativeInteger(snapshot.xpEarned, 'xpEarned');
}

function validateItemProgressSnapshot(snapshot: QuizItemProgressSnapshot): void {
  ensureNonEmpty(snapshot.editionItemId, 'editionItemId');
  if (!isValidQuizItemProgressSequence(snapshot.guessSequence)) {
    throw new QuizRunPersistenceError(
      'Item progress must contain only unique unresolved guesses from 1 to 3',
      { code: 'quiz_run_progress_invalid' }
    );
  }
}

function errorProperty(error: unknown, key: string): unknown {
  return error && typeof error === 'object' ? (error as Record<string, unknown>)[key] : undefined;
}

function errorCode(error: unknown): string | undefined {
  const code = errorProperty(error, 'code');
  return typeof code === 'string' ? code : undefined;
}

function errorStatus(error: unknown): number | undefined {
  const status = errorProperty(error, 'status') ?? errorProperty(error, 'statusCode');
  return typeof status === 'number' ? status : undefined;
}

/** Retry only transient transport/database failures; conflicts and validation stay terminal. */
export function isRetryableQuizRunPersistenceError(error: unknown): boolean {
  if (errorProperty(error, 'retryable') === true) return true;
  const code = errorCode(error);
  const status = errorStatus(error);

  if (status === 408 || status === 429 || (status !== undefined && status >= 500)) return true;
  if (
    code &&
    (/^08/.test(code) ||
      /^53/.test(code) ||
      ['40001', '40P01', '57P01', '57P02', '57P03'].includes(code))
  ) {
    return true;
  }

  if (error instanceof TypeError) return true;
  if (
    code &&
    ['ECONNABORTED', 'ECONNRESET', 'ETIMEDOUT', 'EAI_AGAIN', 'ENETUNREACH'].includes(code)
  ) {
    return true;
  }
  return errorProperty(error, 'name') === 'TimeoutError';
}

function toTerminalError(error: unknown): QuizRunPersistenceError {
  if (error instanceof QuizRunPersistenceError) return error;
  const message = error instanceof Error ? error.message : String(error);
  return new QuizRunPersistenceError(message, {
    code: errorCode(error) ?? 'quiz_run_write_failed',
    status: errorStatus(error),
  });
}

function copyItem(item: PersistedItem): PersistedItem {
  return item.itemType === 'trivia'
    ? {
        ...item,
        guessSequence: [...item.guessSequence],
      }
    : { ...item };
}

function cloneCompletion(completion: QuizCompletion | null): QuizCompletion | null {
  return completion ? { ...completion } : null;
}

function createDefaultPreviewTransport(identity: {
  quizId: string;
  editionId: string;
}): QuizRunPersistenceTransport {
  return {
    writeItemProgress: async () => ({ preview: true }),
    writeTrivia: async () => ({ preview: true }),
    writePoll: async () => ({ preview: true }),
    finalizeCompletion: async ({ items }) => {
      const trivia = items.filter((item) => item.itemType === 'trivia');
      const xpEarned = items.reduce((sum, item) => sum + (item.xpEarned ?? 0), 0);
      const emojiResults = items
        .map((item) => {
          if (item.itemType === 'poll') return '📊';
          const attempts = item.guessSequence.length;
          if (!item.guessSequence.includes(0)) return '❌';
          if (attempts === 1) return '✅';
          if (attempts === 2) return '2️⃣';
          if (attempts === 3) return '3️⃣';
          return '❌';
        })
        .join('');
      const triviaCount = trivia.length;
      const attempts = trivia.reduce(
        (sum, item) => sum + (item.itemType === 'trivia' ? item.guessSequence.length : 0),
        0
      );
      const accuracy =
        triviaCount === 0
          ? 100
          : Math.max(
              0,
              Math.min(
                100,
                Math.round((1 - Math.max(0, attempts - triviaCount) / (triviaCount * 3)) * 100)
              )
            );
      let currentStreak = 0;
      let longestStreak = 0;
      for (const item of items) {
        if (item.itemType === 'poll') continue;
        if (item.guessSequence.length === 1 && item.guessSequence[0] === 0) {
          currentStreak += 1;
          longestStreak = Math.max(longestStreak, currentStreak);
        } else {
          currentStreak = 0;
        }
      }
      return {
        completionId: `preview:${identity.quizId}:${identity.editionId}`,
        quizId: identity.quizId,
        editionId: identity.editionId,
        emojiResults,
        accuracy,
        totalQuestions: items.length,
        xpEarned,
        longestStreak,
      };
    },
  };
}

function normalizeCanonicalItem(item: CanonicalItem): CanonicalItem {
  ensureNonEmpty(item.editionItemId, 'canonical editionItemId');
  if (item.itemType === 'poll') ensureNonEmpty(item.pollDefinitionId, 'canonical pollDefinitionId');
  return item;
}

function itemKey(item: { editionItemId: string }): string {
  return item.editionItemId;
}

function mergeTriviaServerFact(
  current: PersistedTriviaItem | undefined,
  canonical: Extract<CanonicalItem, { itemType: 'trivia' }>
): PersistedTriviaItem | undefined {
  const fact = canonical.fact;
  if (!fact || !Array.isArray(fact.guessSequence)) return current;
  const revision =
    Number.isInteger(fact.revision) && (fact.revision as number) >= 0 ? fact.revision! : 0;
  const server: PersistedTriviaItem = {
    itemType: 'trivia',
    editionItemId: canonical.editionItemId,
    guessSequence: [...fact.guessSequence],
    rating: fact.rating ?? null,
    feedback: fact.feedback ?? null,
    feedbackText: fact.feedbackText ?? null,
    xpEarned: fact.xpEarned ?? 0,
    revision,
    acknowledgedRevision:
      Number.isInteger(fact.acknowledgedRevision) && (fact.acknowledgedRevision as number) >= 0
        ? Math.min(fact.acknowledgedRevision as number, revision)
        : revision,
  };
  if (!current || server.revision > current.revision) return server;
  if (server.revision === current.revision && current.acknowledgedRevision < server.revision) {
    return { ...current, acknowledgedRevision: server.revision };
  }
  if (current.acknowledgedRevision < server.acknowledgedRevision) {
    return {
      ...current,
      acknowledgedRevision: Math.min(server.acknowledgedRevision, current.revision),
    };
  }
  return current;
}

function mergePollServerFact(
  current: PersistedPollItem | undefined,
  canonical: Extract<CanonicalItem, { itemType: 'poll' }>
): PersistedPollItem | undefined {
  const fact = canonical.fact;
  if (!fact || !fact.optionId) return current;
  const revision =
    Number.isInteger(fact.revision) && (fact.revision as number) >= 0 ? fact.revision! : 0;
  const server: PersistedPollItem = {
    itemType: 'poll',
    editionItemId: canonical.editionItemId,
    pollDefinitionId: canonical.pollDefinitionId,
    optionId: fact.optionId,
    sessionId: fact.sessionId ?? null,
    xpEarned: fact.xpEarned,
    revision,
    acknowledgedRevision:
      Number.isInteger(fact.acknowledgedRevision) && (fact.acknowledgedRevision as number) >= 0
        ? Math.min(fact.acknowledgedRevision as number, revision)
        : revision,
  };
  if (!current || server.revision > current.revision) return server;
  if (server.revision === current.revision && current.acknowledgedRevision < server.revision) {
    return { ...current, acknowledgedRevision: server.revision };
  }
  if (current.acknowledgedRevision < server.acknowledgedRevision) {
    return {
      ...current,
      acknowledgedRevision: Math.min(server.acknowledgedRevision, current.revision),
    };
  }
  return current;
}

function createInMemoryStorage(): QuizRunPersistenceStorage {
  const values = new Map<string, string>();
  return {
    getItem: (key) => values.get(key) ?? null,
    setItem: (key, value) => values.set(key, value),
    removeItem: (key) => values.delete(key),
  };
}

/** Construct the one Quiz persistence owner. */
export function createQuizRunPersistence(options: QuizRunPersistenceOptions): QuizRunPersistence {
  const { identity } = options;
  ensureNonEmpty(identity.quizId, 'quizId');
  ensureNonEmpty(identity.editionId, 'editionId');
  ensureNonEmpty(identity.userId, 'userId');
  if (!Number.isInteger(identity.identityGeneration) || identity.identityGeneration < 0) {
    throw new QuizRunPersistenceError('identityGeneration must be a non-negative integer', {
      code: 'quiz_run_identity_invalid',
    });
  }

  const preview = options.preview === true;
  const storage = preview ? createInMemoryStorage() : (options.storage ?? BROWSER_STORAGE);
  const storageKey = buildQuizRunPersistenceStorageKey(identity);
  const scheduler = options.scheduler ?? DEFAULT_SCHEDULER;
  const lifecycle = options.lifecycle ?? DEFAULT_LIFECYCLE;
  const transport = preview
    ? createDefaultPreviewTransport(identity)
    : (options.transport ??
      (() => {
        throw new QuizRunPersistenceError('A Quiz persistence transport is required', {
          code: 'quiz_run_transport_missing',
        });
      })());
  const isIdentityCurrent = options.isIdentityCurrent ?? (() => true);
  if (!preview && !options.isIdentityCurrent) {
    throw new QuizRunPersistenceError(
      'A non-preview Quiz run requires an identity-generation fence',
      { code: 'quiz_run_identity_fence_missing' }
    );
  }
  const journal = loadJournal(storage, storageKey, identity);
  let disposed = false;
  let disposalReason: QuizRunPersistenceCancellationReason = 'disposed';
  let canonicalItems: CanonicalItem[] = [];
  const operationQueues = new Map<string, Promise<unknown>>();
  const operationPromises = new Map<string, Promise<unknown>>();
  const operations = new Set<PendingOperation>();
  const progressCheckpoints = new Map<string, readonly number[]>();
  const progressQueues = new Map<string, Promise<unknown>>();
  const waiters: CompletionWaiter[] = [];
  let completionInFlight: Promise<QuizCompletion | null> | null = null;
  let reconciliationInFlight: Promise<QuizCompletion | null> | null = null;
  let completionRetryAttempts = 0;
  let completionRetryTimer: unknown | null = null;
  let completionRetryParked = false;

  const identityIsCurrent = (): boolean => !disposed && isIdentityCurrent();
  const cancellationError = (): QuizRunPersistenceCancelledError =>
    new QuizRunPersistenceCancelledError(disposed ? disposalReason : 'owner_replaced');

  const persistJournal = (): void => {
    if (!identityIsCurrent()) return;
    storage.setItem(storageKey, encodeQuizRunJournal(journal));
  };

  const updateItem = (item: PersistedItem): void => {
    const index = journal.items.findIndex(
      (candidate) => candidate.editionItemId === item.editionItemId
    );
    if (index < 0) journal.items.push(item);
    else journal.items[index] = item;
  };

  const getItem = (editionItemId: string): PersistedItem | undefined =>
    journal.items.find((item) => item.editionItemId === editionItemId);

  const clearRetryTimer = (operation: PendingOperation): void => {
    if (operation.timer === null) return;
    scheduler.clearTimeout(operation.timer);
    operation.timer = null;
  };

  const scheduleCompletionRetry = (): void => {
    if (disposed || !identityIsCurrent() || completionRetryTimer !== null) return;
    const delay = RETRY_DELAYS_MS[completionRetryAttempts] ?? null;
    completionRetryAttempts += 1;
    if (delay === null) {
      completionRetryParked = true;
      return;
    }
    completionRetryTimer = scheduler.setTimeout(() => {
      completionRetryTimer = null;
      void attemptCompletion().catch(() => undefined);
    }, delay);
  };

  const settleOperation = (operation: PendingOperation, error?: unknown, value?: unknown): void => {
    if (operation.settled) return;
    operation.settled = true;
    clearRetryTimer(operation);
    const operationKey = `${operation.itemId}:${operation.revision}`;
    if (operationPromises.get(operationKey) === operation.promise) {
      operationPromises.delete(operationKey);
    }
    if (error === undefined) operation.resolve(value);
    else operation.reject(error);
  };

  const finishTransport = (operation: PendingOperation, error?: unknown, value?: unknown): void => {
    if (operation.transportFinished) return;
    operation.transportFinished = true;
    operation.inFlight = false;
    operations.delete(operation);
    if (operationQueues.get(operation.itemId) === operation.transportDone) {
      operationQueues.delete(operation.itemId);
    }
    if (error === undefined) operation.resolveTransport();
    else operation.rejectTransport(error);
    if (!operation.settled) settleOperation(operation, error, value);
  };

  const supersedeOperation = (operation: PendingOperation): void => {
    if (operation.settled) return;
    operation.superseded = true;
    const error = new QuizRunPersistenceError('A newer item revision superseded this write', {
      code: 'quiz_run_revision_superseded',
    });
    settleOperation(operation, error);
    if (!operation.inFlight) finishTransport(operation, error);
  };

  const scheduleOperation = (operation: PendingOperation): void => {
    if (operation.settled || operation.parked || disposed || !identityIsCurrent()) return;
    const delay = RETRY_DELAYS_MS[operation.attempts - 1] ?? null;
    if (delay === null) {
      operation.parked = true;
      return;
    }
    operation.timer = scheduler.setTimeout(() => {
      operation.timer = null;
      void runOperation(operation);
    }, delay);
  };

  const markAcknowledged = (operation: PendingOperation): void => {
    if (!identityIsCurrent()) return;
    const current = getItem(operation.itemId);
    if (!current || current.revision !== operation.revision) return;
    if (current.acknowledgedRevision >= operation.revision) return;
    updateItem({ ...current, acknowledgedRevision: operation.revision });
    persistJournal();
  };

  const runOperation = async (operation: PendingOperation): Promise<void> => {
    if (operation.transportFinished) return;
    if (operation.superseded) {
      if (!operation.inFlight) {
        finishTransport(
          operation,
          new QuizRunPersistenceError('A newer item revision superseded this write', {
            code: 'quiz_run_revision_superseded',
          })
        );
      }
      return;
    }
    if (disposed || !identityIsCurrent()) {
      finishTransport(operation, cancellationError());
      return;
    }
    try {
      operation.attempts += 1;
      operation.inFlight = true;
      const result = await operation.run();
      operation.inFlight = false;
      if (operation.superseded) {
        finishTransport(operation);
        return;
      }
      if (!identityIsCurrent()) {
        finishTransport(operation, cancellationError());
        return;
      }
      markAcknowledged(operation);
      finishTransport(operation, undefined, result);
      if (journal.completionRequested) void attemptCompletion().catch(() => undefined);
    } catch (error) {
      operation.inFlight = false;
      if (operation.superseded) {
        finishTransport(operation, error);
        return;
      }
      if (!identityIsCurrent()) {
        finishTransport(operation, cancellationError());
        return;
      }
      if (isRetryableQuizRunPersistenceError(error)) {
        scheduleOperation(operation);
      } else {
        const terminalError = toTerminalError(error);
        finishTransport(operation, terminalError);
        if (journal.completionRequested) void attemptCompletion().catch(() => undefined);
      }
    }
  };

  const enqueueOperation = (item: PersistedItem, run: () => Promise<unknown>): Promise<unknown> => {
    const previous = operationQueues.get(item.editionItemId);
    let resolve!: (value: unknown) => void;
    let reject!: (reason: unknown) => void;
    let resolveTransport!: () => void;
    let rejectTransport!: (reason: unknown) => void;
    const promise = new Promise<unknown>((resolvePromise, rejectPromise) => {
      resolve = resolvePromise;
      reject = rejectPromise;
    });
    const transportDone = new Promise<void>((resolvePromise, rejectPromise) => {
      resolveTransport = resolvePromise;
      rejectTransport = rejectPromise;
    });
    void transportDone.catch(() => undefined);
    const operation: PendingOperation = {
      itemId: item.editionItemId,
      revision: item.revision,
      promise,
      transportDone,
      run,
      resolve,
      reject,
      resolveTransport,
      rejectTransport,
      attempts: 0,
      timer: null,
      parked: false,
      settled: false,
      transportFinished: false,
      inFlight: false,
      superseded: false,
    };
    operations.add(operation);
    operationQueues.set(item.editionItemId, transportDone);
    operationPromises.set(`${item.editionItemId}:${item.revision}`, promise);
    const start = (): void => {
      void runOperation(operation).catch((error) => {
        finishTransport(operation, toTerminalError(error));
      });
    };
    if (previous) {
      // Keep ordering separate from the caller-owned operation promise. If the
      // operation is cancelled, there is no derived rejected promise to orphan.
      void previous.then(start, start);
    } else {
      start();
    }
    return promise;
  };

  const writeItem = (item: PersistedItem): Promise<unknown> => {
    const existing = operationPromises.get(`${item.editionItemId}:${item.revision}`);
    if (existing) return existing;
    const operationPromise = enqueueOperation(item, () =>
      item.itemType === 'trivia'
        ? transport.writeTrivia({
            kind: 'trivia',
            identity: { ...identity },
            snapshot: copyItem(item) as PersistedTriviaItem,
          })
        : transport.writePoll({
            kind: 'poll',
            identity: { ...identity },
            snapshot: copyItem(item) as PersistedPollItem,
          })
    );
    return operationPromise;
  };

  const flushPendingItems = async (): Promise<void> => {
    const pending = journal.items.filter((item) => item.acknowledgedRevision < item.revision);
    if (pending.length === 0) return;
    await Promise.all(pending.map((item) => writeItem(item)));
  };

  const hasScheduledRetry = (): boolean =>
    completionRetryTimer !== null ||
    completionRetryParked ||
    [...operations].some((operation) => operation.timer !== null || operation.parked);

  /** Callers must not hang while a transient write is waiting for its background retry. */
  const awaitRetryableAttempt = async <T>(attempt: Promise<T>): Promise<boolean> => {
    await Promise.resolve();
    await Promise.resolve();
    if (hasScheduledRetry()) {
      void attempt.catch(() => undefined);
      return false;
    }
    await attempt;
    return true;
  };

  const completionItems = (): CompletionItem[] => {
    const canonical = canonicalItems.length
      ? canonicalItems.map((item) => item.editionItemId)
      : journal.items.map((item) => item.editionItemId);
    return canonical.flatMap((editionItemId) => {
      const item = getItem(editionItemId);
      return item ? [copyItem(item) as CompletionItem] : [];
    });
  };

  const reconcileStoredCompletion = async (): Promise<QuizCompletion | null> => {
    if (!journal.completion) return null;
    if (reconciliationInFlight) return reconciliationInFlight;
    if (journal.completionReconciled) {
      if (!preview) storage.removeItem(storageKey);
      return cloneCompletion(journal.completion);
    }

    reconciliationInFlight = (async () => {
      if (!identityIsCurrent() || !journal.completion) return null;
      if (!preview && options.reconcileCompletion) {
        await options.reconcileCompletion({ ...journal.completion });
      }
      if (!identityIsCurrent() || !journal.completion) return null;
      journal.completionReconciled = true;
      persistJournal();
      if (!preview) storage.removeItem(storageKey);
      return cloneCompletion(journal.completion);
    })();
    try {
      return await reconciliationInFlight;
    } finally {
      reconciliationInFlight = null;
    }
  };

  const attemptCompletion = async (): Promise<QuizCompletion | null> => {
    if (completionInFlight) return completionInFlight;
    if (!journal.completionRequested) return null;
    if (journal.completion) {
      const reconciled = await reconcileStoredCompletion();
      if (reconciled) resolveWaiters(reconciled);
      return reconciled;
    }
    if (!identityIsCurrent()) return null;
    const items = completionItems();
    if (canonicalItems.length === 0 || items.length !== canonicalItems.length) {
      return null;
    }

    completionInFlight = (async () => {
      try {
        const itemsFlushed = await awaitRetryableAttempt(flushPendingItems());
        if (!itemsFlushed) {
          resolveWaiters(null);
          return null;
        }
        if (!identityIsCurrent()) return null;
        const completion = await transport.finalizeCompletion({
          identity: { ...identity },
          items: completionItems(),
        });
        if (!identityIsCurrent()) return null;
        journal.completion = { ...completion };
        journal.completionReconciled = false;
        persistJournal();
        const reconciled = await reconcileStoredCompletion();
        completionRetryAttempts = 0;
        completionRetryParked = false;
        resolveWaiters(reconciled);
        return reconciled;
      } catch (error) {
        const terminalError = toTerminalError(error);
        if (isRetryableQuizRunPersistenceError(error) && identityIsCurrent()) {
          scheduleCompletionRetry();
          const durableCompletion = cloneCompletion(journal.completion);
          resolveWaiters(durableCompletion);
          return durableCompletion;
        }
        rejectWaiters(terminalError);
        throw terminalError;
      } finally {
        completionInFlight = null;
      }
    })();
    return completionInFlight;
  };

  const retryParked = (): void => {
    for (const operation of operations) {
      if (!operation.parked || operation.settled) continue;
      operation.parked = false;
      operation.attempts = 0;
      void runOperation(operation);
    }
  };

  const onRetrySignal = (): void => {
    if (disposed || !identityIsCurrent()) return;
    retryParked();
    if (completionRetryParked) {
      completionRetryParked = false;
      completionRetryAttempts = 0;
    }
    if (journal.completionRequested) void attemptCompletion().catch(() => undefined);
  };

  const resolveWaiters = (completion: QuizCompletion | null): void => {
    while (waiters.length) waiters.shift()!.resolve(completion);
  };
  const rejectWaiters = (error: unknown): void => {
    while (waiters.length) waiters.shift()!.reject(error);
  };

  const stage = (
    snapshot: TriviaSnapshot | PollSnapshot,
    itemType: 'trivia' | 'poll'
  ): Promise<unknown> => {
    if (!identityIsCurrent()) return Promise.reject(cancellationError());
    if (itemType === 'trivia') validateTriviaSnapshot(snapshot as TriviaSnapshot);
    else validatePollSnapshot(snapshot as PollSnapshot);
    const current = getItem(snapshot.editionItemId);
    const revision = (current?.revision ?? 0) + 1;
    for (const operation of operations) {
      if (operation.itemId === snapshot.editionItemId && operation.revision < revision) {
        supersedeOperation(operation);
      }
    }
    const item: PersistedItem =
      itemType === 'trivia'
        ? {
            ...(snapshot as TriviaSnapshot),
            itemType,
            revision,
            acknowledgedRevision: 0,
            guessSequence: [...(snapshot as TriviaSnapshot).guessSequence],
            rating: (snapshot as TriviaSnapshot).rating ?? null,
            feedback: (snapshot as TriviaSnapshot).feedback ?? null,
            feedbackText: (snapshot as TriviaSnapshot).feedbackText ?? null,
          }
        : {
            ...(snapshot as PollSnapshot),
            itemType,
            revision,
            acknowledgedRevision: 0,
            sessionId: (snapshot as PollSnapshot).sessionId ?? null,
          };
    updateItem(item);
    persistJournal();
    const promise = writeItem(item);
    void promise.catch(() => undefined);
    if (journal.completionRequested) void attemptCompletion().catch(() => undefined);
    return promise;
  };

  const recordItemProgress = (snapshot: QuizItemProgressSnapshot): Promise<unknown> => {
    if (!identityIsCurrent()) return Promise.reject(cancellationError());
    validateItemProgressSnapshot(snapshot);

    const nextSequence = [...snapshot.guessSequence];
    const currentSequence = progressCheckpoints.get(snapshot.editionItemId);
    if (currentSequence) {
      if (isSequencePrefix(nextSequence, currentSequence)) {
        return Promise.resolve({ deduplicated: true });
      }
      if (!isSequencePrefix(currentSequence, nextSequence)) {
        return Promise.reject(
          new QuizRunPersistenceError('Item progress conflicts with the recorded guess sequence', {
            code: 'quiz_run_progress_conflict',
          })
        );
      }
    }

    progressCheckpoints.set(snapshot.editionItemId, nextSequence);
    const previous = progressQueues.get(snapshot.editionItemId);
    const write = (previous ?? Promise.resolve())
      .catch(() => undefined)
      .then(() => {
        if (!identityIsCurrent()) {
          throw cancellationError();
        }
        return transport.writeItemProgress({
          identity: { ...identity },
          snapshot: {
            editionItemId: snapshot.editionItemId,
            guessSequence: [...nextSequence],
          },
        });
      });
    progressQueues.set(snapshot.editionItemId, write);
    void write
      .catch(() => {
        const latest = progressCheckpoints.get(snapshot.editionItemId);
        if (
          latest &&
          isSequencePrefix(latest, nextSequence) &&
          isSequencePrefix(nextSequence, latest)
        ) {
          progressCheckpoints.delete(snapshot.editionItemId);
        }
      })
      .finally(() => {
        if (progressQueues.get(snapshot.editionItemId) === write) {
          progressQueues.delete(snapshot.editionItemId);
        }
      });
    return write;
  };

  const recover = async (items: readonly CanonicalItem[]): Promise<QuizRunPersistenceSnapshot> => {
    if (disposed) return getSnapshot();
    if (!identityIsCurrent()) return getSnapshot();
    const normalized = items.map(normalizeCanonicalItem);
    const unique = new Set(normalized.map(itemKey));
    if (unique.size !== normalized.length) {
      throw new QuizRunPersistenceError('Canonical item ids must be unique', {
        code: 'quiz_run_canonical_items_invalid',
      });
    }
    canonicalItems = normalized.map((item) => ({ ...item }));
    for (const canonical of normalized) {
      const current = getItem(canonical.editionItemId);
      const merged =
        canonical.itemType === 'trivia'
          ? mergeTriviaServerFact(current?.itemType === 'trivia' ? current : undefined, canonical)
          : mergePollServerFact(current?.itemType === 'poll' ? current : undefined, canonical);
      if (merged) updateItem(merged);

      const hasResolvedFact =
        canonical.itemType === 'trivia'
          ? Array.isArray(canonical.fact?.guessSequence)
          : typeof canonical.fact?.optionId === 'string' && canonical.fact.optionId.length > 0;
      if (hasResolvedFact) {
        progressCheckpoints.delete(canonical.editionItemId);
      } else if (canonical.progress) {
        validateItemProgressSnapshot(canonical.progress);
        progressCheckpoints.set(
          canonical.editionItemId,
          reconcileQuizItemProgress(
            canonical.progress.guessSequence,
            progressCheckpoints.get(canonical.editionItemId)
          )
        );
      }
    }
    persistJournal();
    retryParked();
    try {
      await awaitRetryableAttempt(flushPendingItems());
      if (journal.completion) {
        await awaitRetryableAttempt(reconcileStoredCompletion());
      } else if (journal.completionRequested) {
        await awaitRetryableAttempt(attemptCompletion());
      }
    } catch (error) {
      if (!isRetryableQuizRunPersistenceError(error)) throw error;
      // The journal is intentionally retained; the next lifecycle/recovery signal retries it.
    }
    return getSnapshot();
  };

  if (!preview) {
    lifecycle.addEventListener?.('online', onRetrySignal);
    lifecycle.addEventListener?.('focus', onRetrySignal);
  }

  function loadJournal(
    configuredStorage: QuizRunPersistenceStorage,
    key: string,
    configuredIdentity: QuizRunPersistenceIdentity
  ): QuizRunJournal {
    const decoded = decodeQuizRunJournal(configuredStorage.getItem(key));
    if (!decoded) return createEmptyJournal(configuredIdentity);
    if (
      decoded.identity.quizId !== configuredIdentity.quizId ||
      decoded.identity.editionId !== configuredIdentity.editionId ||
      decoded.identity.userId !== configuredIdentity.userId ||
      decoded.identity.isAnonymous !== configuredIdentity.isAnonymous
    ) {
      return createEmptyJournal(configuredIdentity);
    }
    return cloneJournal(decoded);
  }

  const getSnapshot = (): QuizRunPersistenceSnapshot => ({
    version: 1,
    storageKey,
    identity: { ...identity },
    items: journal.items.map(copyItem),
    completionRequested: journal.completionRequested,
    completion: cloneCompletion(journal.completion),
    completionReconciled: journal.completionReconciled,
    disposed,
  });

  const requestCompletion = (): Promise<QuizCompletion | null> => {
    if (disposed || !identityIsCurrent()) return Promise.resolve(null);
    journal.completionRequested = true;
    persistJournal();
    if (journal.completion) return reconcileStoredCompletion();
    const promise = new Promise<QuizCompletion | null>((resolve, reject) => {
      waiters.push({ resolve, reject });
    });
    retryParked();
    if (completionRetryParked) {
      completionRetryParked = false;
      completionRetryAttempts = 0;
    }
    void attemptCompletion().catch(() => undefined);
    return promise;
  };

  const dispose = (reason: QuizRunPersistenceCancellationReason = 'disposed'): void => {
    if (disposed) return;
    disposalReason = reason;
    disposed = true;
    const cancellation = new QuizRunPersistenceCancelledError(reason);
    if (!preview) {
      lifecycle.removeEventListener?.('online', onRetrySignal);
      lifecycle.removeEventListener?.('focus', onRetrySignal);
    }
    if (completionRetryTimer !== null) {
      scheduler.clearTimeout(completionRetryTimer);
      completionRetryTimer = null;
    }
    for (const operation of operations) {
      clearRetryTimer(operation);
      settleOperation(operation, cancellation);
      if (!operation.inFlight) finishTransport(operation, cancellation);
    }
    rejectWaiters(cancellation);
  };

  return {
    recordItemProgress,
    stageTrivia: (snapshot) => stage(snapshot, 'trivia'),
    stagePoll: (snapshot) => stage(snapshot, 'poll'),
    requestCompletion,
    recover,
    getSnapshot,
    dispose,
  };
}

export function createPreviewQuizRunPersistence(
  options: Omit<QuizRunPersistenceOptions, 'transport' | 'storage' | 'scheduler' | 'preview'>
): QuizRunPersistence {
  return createQuizRunPersistence({ ...options, preview: true });
}
